import type { DBGHubPayload } from "@/data";

export interface IChartDataItem {
  color?: string
  label: string
  value: number
  percentage?: number
}

export function formatCurrency(amount: number, hidden?: boolean) {
  if (hidden) return '$\u23FA\u23FA\u23FA\u23FA';

  if (!amount) return '$0';

  const inThousands = Math.abs(amount) >= 1_000_000;
  const divisor = inThousands ? 1_000 : 1;
  // Round to the nearest whole number
  const roundedAmount = Math.round(amount / divisor);
  const formattedAmount = new Intl.NumberFormat('en-US').format(roundedAmount);

  return `$${formattedAmount}${inThousands ? 'K' : ''}`;
}


export function formatPercent(amount: number, decimal: number = 2): string {
  if(!amount)
    return '0%'

  return `${(amount * 100).toFixed(decimal)}%`;
}

export function formatNumber(amount: number, hidden?: boolean) {
  if (!amount) return '0';

  if (hidden) return '\u23FA\u23FA\u23FA\u23FA';

  // Round to the nearest whole number
  const roundedAmount = Math.round(amount);

  return `${roundedAmount}`;
}

export function transformDataToRadialChartFormat(data: any, portfolio: string) {
  type PortfolioKeys = 'terms' | 'rebates' | 'bonusStock' | 'signOnBonus' | 'marketingSupport';

  const rootStyle = getComputedStyle(document.documentElement);

  const colors = [
    rootStyle.getPropertyValue(`--color-${portfolio}-1`).trim(),
    rootStyle.getPropertyValue(`--color-${portfolio}-2`).trim(),
    rootStyle.getPropertyValue(`--color-${portfolio}-3`).trim(),
    rootStyle.getPropertyValue(`--color-${portfolio}-4`).trim(),
    rootStyle.getPropertyValue(`--color-${portfolio}-5`).trim()
  ];

  if (portfolio === 'axe' && Array.isArray(data)) {
    // Handle special case for "axe" portfolio
    const maxValue = Math.max(...data.map((item: any) => item.claimsEarnedValue || 0));

    const finalData = data.map((item: any, index: number) => ({
      label: item.brandName || 'Unknown',
      value: item.claimsEarnedValue || 0,
      color: colors[index % colors.length], // Cycle through the colors
      percentage: maxValue > 0 ? parseFloat(((item.claimsEarnedValue / maxValue) * 100).toFixed(0)) : 0
    })).filter(i => i.percentage > 0);

    return finalData;
  }

  // Default case for other portfolios
  const keyMapping: { key: PortfolioKeys; label: string; color: string }[] = [
    { key: 'terms', label: 'Terms', color: colors[0]! },
    { key: 'rebates', label: 'Rebates', color: colors[1]! },
    { key: 'marketingSupport', label: 'Marketing Support', color: colors[2]! },
    { key: 'bonusStock', label: 'Bonus Stock', color: colors[3]! },
    { key: 'signOnBonus', label: 'Sign On Bonus', color: colors[4]! }
  ];

  const transformedData = keyMapping.map(({ key, label, color }) => ({
    label,
    value: data[key] || 0,
    color,
    percentage: 0
  }));

  const maxValue = Math.max(...transformedData.map((item) => item.value));

  const finalData = transformedData.map((item) => ({
    ...item,
    percentage: maxValue > 0 ? parseFloat(((item.value / maxValue) * 100).toFixed(0)) : 0
  })).filter(i => i.percentage > 0);

  return finalData;
}

export const addColorProperty = (data: { label: string; value: number }[]) => {
  return data.map(item => {
    let color = '';

    switch (item.label) {
      case 'Arrotex':
        color = '#2AACE2';
        break;
      case 'VidaCorp':
        color = '#2CCE86';
        break;
      case 'PharmaPrograms':
        color = '#5A5BC1';
        break;
      default:
        color = '#939393';
    }

    return { ...item, color };
  });
};
export function getImageUrl(name: string): string {
  return new URL(`../../assets/${name}`, import.meta.url).href
}

export const transformDbgDataToRadialChartFormat = (payload: DBGHubPayload): IChartDataItem[] => {
  const rootStyle = getComputedStyle(document.documentElement);
  return [
    {
      label: 'Arrotex',
      value: payload.arrotex.value,
      color: rootStyle.getPropertyValue(`--color-arrotex-2`).trim(),
      percentage: payload.arrotex.value/payload.portfolioNetValue.dbgValue * 100
    },
    {
      label: 'VidaCorp',
      value: payload.vidacorp.value,
      color: rootStyle.getPropertyValue(`--color-vidacorp-2`).trim(),
      percentage: payload.vidacorp.value/payload.portfolioNetValue.dbgValue * 100
    },
    {
      label: 'Axe',
      value: payload.pharmaprograms.reduce((a, b) => a + b.claimsEarnedValue,0),
      color: rootStyle.getPropertyValue(`--color-axe-2`).trim(),
      percentage: payload.pharmaprograms.reduce((a, b) => a + b.claimsEarnedValue,0)/payload.portfolioNetValue.dbgValue * 100
    }
  ];
}
